import { isAnaFlightNumber, isJalFlightNumber } from './same_day_restricted_ticket';

export type DomesticAirFareClass = 'ana_economy' | 'ana_premium' | 'jal_economy' | 'jal_class_j' | 'jal_first';

export type DomesticAirFareCarrier = 'ANA' | 'JAL';

export type DomesticAirFareReservationChangeFee = '無料' | '有料' | '-';

export type DomesticAirFareDetail = {
  carrier: DomesticAirFareCarrier;
  fareClass: DomesticAirFareClass;
  fareName: string; // マスタ運賃名（フレックス/シンプル/セイバー等）
  reservationChangeFee: DomesticAirFareReservationChangeFee; // 予約変更手数料（CSV「変更手数料」列: なし→無料、あり→有料、—→-）
  refund: string; // 払い戻し（UIデザイン確定により全運賃「有料」）
  carryOnBaggage: string; // 機内手荷物
  checkedBaggage: string; // 預け入れ手荷物
  notes: string[]; // 補足注記（CSVの備考欄。'-'は[]）
  seatSelectionRestricted: boolean; // CSV「事前座席指定」=「出発時刻24時間前から可」
};

// 備考欄の共通文言（tooltip_design.png の文言に合わせる）
const NOTE_FARE_DIFFERENCE = '※ 変更後の旅券に差額が発生する場合、差額の支払いが必要になる場合がございます。';
const NOTE_KABU_TICKET = '※ 優待券は弊社で用意しますので、株主以外のお客様でもご利用可能です。';
const NOTE_SEAT_SELECTION_24H = '※ 座席は出発の24時間前から指定可能です。';
const NOTE_ROUND_TRIP_REQUIRED = '※国内線の往復航空券を同時に予約手配が必要です。';

// 運賃詳細マスタ（原本: works/tickets/AITRAVEL-6659/fare_list.csv、2025/5/19搭乗分以降の新運賃体系）
// 表示に使う4項目（予約変更手数料/払い戻し/機内手荷物/預け入れ手荷物）＋備考欄＋事前座席指定制限のみ収録
export const DOMESTIC_AIR_FARE_DETAILS: DomesticAirFareDetail[] = [
  // ANA エコノミー（普通席）
  {
    carrier: 'ANA',
    fareClass: 'ana_economy',
    fareName: 'フレックス',
    reservationChangeFee: '無料',
    refund: '有料',
    carryOnBaggage: '10kg以内',
    checkedBaggage: '2個/23kg',
    notes: [NOTE_FARE_DIFFERENCE],
    seatSelectionRestricted: false
  },
  {
    carrier: 'ANA',
    fareClass: 'ana_economy',
    fareName: '株主優待割引',
    reservationChangeFee: '無料',
    refund: '有料',
    carryOnBaggage: '10kg以内',
    checkedBaggage: '2個/23kg',
    notes: [NOTE_KABU_TICKET, NOTE_FARE_DIFFERENCE],
    seatSelectionRestricted: false
  },
  {
    carrier: 'ANA',
    fareClass: 'ana_economy',
    fareName: 'スタンダード',
    reservationChangeFee: '有料',
    refund: '有料',
    carryOnBaggage: '10kg以内',
    checkedBaggage: '2個/23kg',
    notes: [NOTE_FARE_DIFFERENCE],
    seatSelectionRestricted: false
  },
  {
    carrier: 'ANA',
    fareClass: 'ana_economy',
    fareName: 'シンプル',
    reservationChangeFee: '-',
    refund: '有料',
    carryOnBaggage: '10kg以内',
    checkedBaggage: '1個/23kg',
    notes: [NOTE_SEAT_SELECTION_24H],
    seatSelectionRestricted: true
  },
  {
    carrier: 'ANA',
    fareClass: 'ana_economy',
    fareName: 'セール',
    reservationChangeFee: '-',
    refund: '有料',
    carryOnBaggage: '10kg以内',
    checkedBaggage: '1個/23kg',
    notes: [NOTE_SEAT_SELECTION_24H],
    seatSelectionRestricted: true
  },
  // ANA プレミアム（CSV「ファースト」行。実データのプレミアム系seat_typeに対応）
  {
    carrier: 'ANA',
    fareClass: 'ana_premium',
    fareName: 'フレックス',
    reservationChangeFee: '無料',
    refund: '有料',
    carryOnBaggage: '10kg以内',
    checkedBaggage: '3個/32kg',
    notes: [NOTE_FARE_DIFFERENCE],
    seatSelectionRestricted: false
  },
  {
    carrier: 'ANA',
    fareClass: 'ana_premium',
    fareName: '株主優待割引',
    reservationChangeFee: '無料',
    refund: '有料',
    carryOnBaggage: '10kg以内',
    checkedBaggage: '3個/32kg',
    notes: [NOTE_KABU_TICKET, NOTE_FARE_DIFFERENCE],
    seatSelectionRestricted: false
  },
  {
    carrier: 'ANA',
    fareClass: 'ana_premium',
    fareName: 'スタンダード',
    reservationChangeFee: '有料',
    refund: '有料',
    carryOnBaggage: '10kg以内',
    checkedBaggage: '3個/32kg',
    notes: [NOTE_FARE_DIFFERENCE],
    seatSelectionRestricted: false
  },
  {
    carrier: 'ANA',
    fareClass: 'ana_premium',
    fareName: 'シンプル',
    reservationChangeFee: '-',
    refund: '有料',
    carryOnBaggage: '10kg以内',
    checkedBaggage: '2個/32kg',
    notes: [NOTE_SEAT_SELECTION_24H],
    seatSelectionRestricted: true
  },
  {
    carrier: 'ANA',
    fareClass: 'ana_premium',
    fareName: 'セール',
    reservationChangeFee: '-',
    refund: '有料',
    carryOnBaggage: '10kg以内',
    checkedBaggage: '2個/32kg',
    notes: [NOTE_SEAT_SELECTION_24H],
    seatSelectionRestricted: true
  },
  // JAL 普通席
  {
    carrier: 'JAL',
    fareClass: 'jal_economy',
    fareName: 'フレックス',
    reservationChangeFee: '無料',
    refund: '有料',
    carryOnBaggage: '10kg以内',
    checkedBaggage: '個数制限無/20kg',
    notes: [NOTE_FARE_DIFFERENCE],
    seatSelectionRestricted: false
  },
  {
    carrier: 'JAL',
    fareClass: 'jal_economy',
    fareName: '株主優待割引',
    reservationChangeFee: '無料',
    refund: '有料',
    carryOnBaggage: '10kg以内',
    checkedBaggage: '個数制限無/20kg',
    notes: [NOTE_KABU_TICKET, NOTE_FARE_DIFFERENCE],
    seatSelectionRestricted: false
  },
  {
    carrier: 'JAL',
    fareClass: 'jal_economy',
    fareName: 'セイバー',
    reservationChangeFee: '-',
    refund: '有料',
    carryOnBaggage: '10kg以内',
    checkedBaggage: '個数制限無/20kg',
    notes: [],
    seatSelectionRestricted: false
  },
  {
    carrier: 'JAL',
    fareClass: 'jal_economy',
    fareName: 'スペシャルセイバー',
    reservationChangeFee: '-',
    refund: '有料',
    carryOnBaggage: '10kg以内',
    checkedBaggage: '個数制限無/20kg',
    notes: [],
    seatSelectionRestricted: false
  },
  {
    carrier: 'JAL',
    fareClass: 'jal_economy',
    fareName: '往復セイバー',
    reservationChangeFee: '-',
    refund: '有料',
    carryOnBaggage: '10kg以内',
    checkedBaggage: '個数制限無/20kg',
    notes: [NOTE_ROUND_TRIP_REQUIRED],
    seatSelectionRestricted: false
  },
  // JAL クラスJ
  {
    carrier: 'JAL',
    fareClass: 'jal_class_j',
    fareName: 'フレックス',
    reservationChangeFee: '無料',
    refund: '有料',
    carryOnBaggage: '10kg以内',
    checkedBaggage: '個数制限無/20kg',
    notes: [NOTE_FARE_DIFFERENCE],
    seatSelectionRestricted: false
  },
  {
    carrier: 'JAL',
    fareClass: 'jal_class_j',
    fareName: '株主優待割引',
    reservationChangeFee: '無料',
    refund: '有料',
    carryOnBaggage: '10kg以内',
    checkedBaggage: '個数制限無/20kg',
    notes: [NOTE_KABU_TICKET, NOTE_FARE_DIFFERENCE],
    seatSelectionRestricted: false
  },
  {
    carrier: 'JAL',
    fareClass: 'jal_class_j',
    fareName: 'セイバー',
    reservationChangeFee: '-',
    refund: '有料',
    carryOnBaggage: '10kg以内',
    checkedBaggage: '個数制限無/20kg',
    notes: [],
    seatSelectionRestricted: false
  },
  {
    carrier: 'JAL',
    fareClass: 'jal_class_j',
    fareName: 'スペシャルセイバー',
    reservationChangeFee: '-',
    refund: '有料',
    carryOnBaggage: '10kg以内',
    checkedBaggage: '個数制限無/20kg',
    notes: [],
    seatSelectionRestricted: false
  },
  {
    carrier: 'JAL',
    fareClass: 'jal_class_j',
    fareName: '往復セイバー',
    reservationChangeFee: '-',
    refund: '有料',
    carryOnBaggage: '10kg以内',
    checkedBaggage: '個数制限無/20kg',
    notes: [NOTE_ROUND_TRIP_REQUIRED],
    seatSelectionRestricted: false
  },
  // JAL ファーストクラス
  {
    carrier: 'JAL',
    fareClass: 'jal_first',
    fareName: 'フレックス',
    reservationChangeFee: '無料',
    refund: '有料',
    carryOnBaggage: '10kg以内',
    checkedBaggage: '個数制限無/45kg',
    notes: [NOTE_FARE_DIFFERENCE],
    seatSelectionRestricted: false
  },
  {
    carrier: 'JAL',
    fareClass: 'jal_first',
    fareName: '株主優待割引',
    reservationChangeFee: '無料',
    refund: '有料',
    carryOnBaggage: '10kg以内',
    checkedBaggage: '個数制限無/45kg',
    notes: [NOTE_KABU_TICKET, NOTE_FARE_DIFFERENCE],
    seatSelectionRestricted: false
  },
  {
    carrier: 'JAL',
    fareClass: 'jal_first',
    fareName: 'セイバー',
    reservationChangeFee: '-',
    refund: '有料',
    carryOnBaggage: '10kg以内',
    checkedBaggage: '個数制限無/45kg',
    notes: [],
    seatSelectionRestricted: false
  },
  {
    carrier: 'JAL',
    fareClass: 'jal_first',
    fareName: 'スペシャルセイバー',
    reservationChangeFee: '-',
    refund: '有料',
    carryOnBaggage: '10kg以内',
    checkedBaggage: '個数制限無/45kg',
    notes: [],
    seatSelectionRestricted: false
  },
  {
    carrier: 'JAL',
    fareClass: 'jal_first',
    fareName: '往復セイバー',
    reservationChangeFee: '-',
    refund: '有料',
    carryOnBaggage: '10kg以内',
    checkedBaggage: '個数制限無/45kg',
    notes: [NOTE_ROUND_TRIP_REQUIRED],
    seatSelectionRestricted: false
  }
];

// ANA新運賃名の表示開始日。select_repository.ts の ANA_FARE_LABEL_AVAILABLE_FROM と同値。
// ハブモジュール（select_repository.ts）からの値importは循環参照になるため、葉側に同値定数を定義する
const ANA_FARE_DETAIL_AVAILABLE_FROM = '2026-05-19';

const toDateString = (input: string | Date | null | undefined): string | null => {
  if (!input) return null;
  if (input instanceof Date) {
    const y = input.getFullYear();
    const m = String(input.getMonth() + 1).padStart(2, '0');
    const d = String(input.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  return typeof input === 'string' && input.length >= 10 ? input.slice(0, 10) : null;
};

const isKabuTicketType = (ticketType: string): boolean => ticketType === 'kabu' || ticketType.includes('株主優待');

// ANA: スクレイピング系内部コード（'normal' / 'changeable_*' / 'unchangeable_*' / 'kabu'）を
// ANA運賃ブランド名にマッピングする（select_repository.ts の mapTicketTypeForFare と同等ロジック）。
// ※ 内部コード 'unchangeable_*' はセール運賃でもシンプルに丸まるが、CSV上シンプルとセールの
//   4項目・注記・座席制限は完全一致のため表示内容に誤りはない
const normalizeAnaFareName = (ticketType: string): string => {
  if (isKabuTicketType(ticketType)) return '株主優待割引';
  // 'unchangeable_' 判定を 'changeable_' より先に置く（順序を逆にすると誤マッチ）
  if (ticketType.startsWith('unchangeable_')) return 'シンプル';
  if (ticketType === 'normal' || ticketType.startsWith('changeable_')) return 'フレックス';
  return ticketType;
};

const JAL_FARE_NAMES = DOMESTIC_AIR_FARE_DETAILS.filter(detail => detail.carrier === 'JAL').map(
  detail => detail.fareName
);

// JAL: 'kabu'（正規化済み株主優待コード）と生運賃名「株主割引」→ マスタ名「株主優待割引」。
// 生運賃名の完全一致はゲート不要で許可。
// Skyticket経路（株主優待/通常運賃同時表示OFF）はticket_typeが内部コード
// （変更可='normal'/'kabu'、変更不可='unchangeable_<数字>'）で届くため、
// ANAと同様に内部コードをマスタ運賃名へマッピングする（新運賃体系の開始日以降のみ有効。
// 日付が無い/開始日前は旧運賃体系の誤表示防止のため null（ⓘ非表示））。
// マスタ未収録名（「株主」を含む未知の運賃名等）は null のまま
const JAL_KABU_TICKET_TYPES = ['kabu', '株主割引', '株主優待割引'];

const normalizeJalFareName = (
  ticketType: string,
  departureDate: string | Date | null | undefined
): string | null => {
  if (JAL_KABU_TICKET_TYPES.includes(ticketType)) return '株主優待割引';
  if (JAL_FARE_NAMES.includes(ticketType)) return ticketType;

  const departureDateStr = toDateString(departureDate);
  if (!departureDateStr || departureDateStr < ANA_FARE_DETAIL_AVAILABLE_FROM) return null;

  // 'unchangeable_' 判定を 'changeable_' より先に置く（順序を逆にすると誤マッチ）
  // unchangeable_* はセイバーに丸める: セイバー/スペシャルセイバーはCSV上、
  // 表示4項目・注記・座席制限が完全一致のため表示内容に誤りはない（ANAのシンプル丸めと同じ考え方）
  if (ticketType.startsWith('unchangeable_')) return 'セイバー';
  if (ticketType === 'normal' || ticketType.startsWith('changeable_')) return 'フレックス';
  return null;
};

// キャリア別に ticket_type をマスタ運賃名へ正規化する（クラス非依存）。
// ANAはマスタ未収録の生値もそのまま返す（既存のANA運賃名表示と同挙動）。JALはマスタ収録名のみ
export const resolveDomesticAirFareName = (
  carrier: DomesticAirFareCarrier,
  ticketType: string | null | undefined,
  departureDate?: string | Date | null
): string | null => {
  if (!ticketType) return null;
  return carrier === 'ANA' ? normalizeAnaFareName(ticketType) : normalizeJalFareName(ticketType, departureDate);
};

const ANA_ECONOMY_SEAT_TYPES = ['普通席', 'エコノミークラス', 'エコノミー'];
const ANA_PREMIUM_SEAT_TYPES = [
  'プレミアム',
  'プレミアム席',
  'プレミアムクラス',
  'ファースト',
  'ファーストクラス'
];
const JAL_FIRST_SEAT_TYPES = ['ファースト', 'ファーストクラス'];

const resolveFareClass = (carrier: DomesticAirFareCarrier, seatType: string): DomesticAirFareClass | null => {
  if (carrier === 'ANA') {
    if (ANA_ECONOMY_SEAT_TYPES.includes(seatType)) return 'ana_economy';
    if (ANA_PREMIUM_SEAT_TYPES.includes(seatType)) return 'ana_premium';
    return null;
  }
  if (seatType === '普通席') return 'jal_economy';
  if (seatType === 'クラスJ') return 'jal_class_j';
  if (JAL_FIRST_SEAT_TYPES.includes(seatType)) return 'jal_first';
  return null;
};

export type ResolveDomesticAirFareDetailInput = {
  ticketType?: string | null;
  seatType?: string | null;
  flightNumber?: string | null;
  departureDate?: string | Date | null;
};

// (キャリア判定 → 運賃名正規化 → ANA日付ゲート → クラス解決 → マスタ完全一致) => エントリ or null
export const resolveDomesticAirFareDetail = (
  input: ResolveDomesticAirFareDetailInput
): DomesticAirFareDetail | null => {
  const { ticketType, seatType, flightNumber, departureDate } = input;
  if (!ticketType || !seatType) return null;

  const carrier: DomesticAirFareCarrier | null = isAnaFlightNumber(flightNumber)
    ? 'ANA'
    : isJalFlightNumber(flightNumber)
    ? 'JAL'
    : null;
  if (!carrier) return null;

  const fareName = resolveDomesticAirFareName(carrier, ticketType, departureDate);
  if (!fareName) return null;

  // ANAは新運賃体系の開始日以降の搭乗分のみ対象（既存の運賃名表示ゲートと同条件）。
  // JALは生値一致・kabuはゲート不要、内部コード由来は normalizeJalFareName 内で同日付ゲート済み
  if (carrier === 'ANA') {
    const departureDateStr = toDateString(departureDate);
    if (!departureDateStr || departureDateStr < ANA_FARE_DETAIL_AVAILABLE_FROM) return null;
  }

  const fareClass = resolveFareClass(carrier, seatType);
  if (!fareClass) return null;

  return (
    DOMESTIC_AIR_FARE_DETAILS.find(
      detail => detail.carrier === carrier && detail.fareClass === fareClass && detail.fareName === fareName
    ) || null
  );
};
